import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDate } from "../lib/utils";
import {
  fetchUsers,
  deleteUser,
} from "../redux/slices/userSlice";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function Users() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const authUser = useSelector((state) => state.auth.user);
  const isSuperAdmin = authUser?.role === "super_admin";
  const { users, pagination, listLoading } = useSelector(
    (state) => state.users
  );

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const limit = 10;

  useEffect(() => {
    const q = searchParams.get("search");
    if (q != null) {
      setSearchInput(q);
      setSearch(q);
      setPage(1);
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(
      fetchUsers({
        page,
        limit,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(role ? { role } : {}),
        ...(status ? { status } : {}),
      })
    );
  }, [dispatch, page, search, role, status]);

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const displayName = (u) => {
    const profiles = u.profiles || [];
    const primary =
      profiles.find((p) => p.is_primary) || profiles[0];
    return (
      primary?.full_name?.trim() ||
      primary?.profile_name?.trim() ||
      "—"
    );
  };

  const handleDeleteConfirm = async () => {
    if (!toDelete) return;
    if (authUser?.id === toDelete.id) {
      toast({
        title: "Not allowed",
        description: "You cannot delete your own account from here.",
        variant: "destructive",
      });
      setDeleteOpen(false);
      return;
    }
    try {
      await dispatch(deleteUser(toDelete.id)).unwrap();
      toast({ title: "User deleted" });
      setDeleteOpen(false);
      setToDelete(null);
      dispatch(
        fetchUsers({
          page,
          limit,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(role ? { role } : {}),
          ...(status ? { status } : {}),
        })
      );
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Delete failed",
        variant: "destructive",
      });
    }
  };

  const totalPages = pagination?.totalPages || 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Users</h1>
        <p className="text-muted-foreground">
          Search, filter, and open accounts to change role or status
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Directory</CardTitle>
          <CardDescription>
            {pagination
              ? `${pagination.total} user(s) total`
              : "Loading…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search email or name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
              />
              <Button type="button" variant="secondary" onClick={applySearch}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select
                value={role || "all"}
                onValueChange={(v) => {
                  setPage(1);
                  setRole(v === "all" ? "" : v);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {isSuperAdmin && (
                    <SelectItem value="super_admin">Super admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Select
                value={status || "all"}
                onValueChange={(v) => {
                  setPage(1);
                  setStatus(v === "all" ? "" : v);
                }}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="pending_verification">
                    Pending verification
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border">
            {listLoading && users.length === 0 ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell>{displayName(u)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {u.role?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {u.status?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {u.created_at || u.createdAt
                          ? formatDate(u.created_at || u.createdAt)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/users/${u.id}`}>View</Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          disabled={authUser?.id === u.id}
                          onClick={() => {
                            setToDelete(u);
                            setDeleteOpen(true);
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {!listLoading && users.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              No users match your filters.
            </p>
          )}

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              {toDelete
                ? `This will remove ${toDelete.email} and related data where the API allows.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
